import styles from "./listing-media-panel.module.css";
import {
    ChevronSmallLeftIcon as ChevronLeftIcon,
    ChevronSmallRightIcon as ChevronRightIcon,
} from "@wags/ui/product-icons.tsx";

import {
    MediaThumbnail,
    MediaThumbnailButton,
} from "@wags/ui/media-thumbnail.tsx";
import { cn } from "@wags/ui/ui-utils";
import { panelFillBackgroundImage } from "@wags/ui/vercel-image.tsx";
import { isVideoMedia } from "@wags/utils/media.ts";
import { type CSSProperties, type ReactNode, useRef, useState } from "react";
import {
    useLayoutBox,
    useUnmountLayout,
    useUnsafeLayoutEffect,
} from "@wags/tanstack-start/react";
import { slideMediaMounted } from "./listing-media-mount";
import { ListingInlineVideo } from "./listing-inline-video";
import { PhotoLoadingIndicator } from "./photo-loading-indicator";

type MediaItem = { url: string; mimeType?: string | null };

const MOUNT_RADIUS = 2;

function mediaKindLabel(media: MediaItem) {
    return isVideoMedia(media) ? "video" : "photo";
}

function revealThumbnail(element: HTMLButtonElement | null) {
    element?.scrollIntoView({
        inline: "nearest",
        block: "nearest",
        behavior: "auto",
    });
}

export type MediaPanelEndSlide = {
    content: ReactNode;
    thumbnail: ReactNode;
    label: string;
};

function nextArrowLabel(
    nextMedia: MediaItem | undefined,
    endSlide: MediaPanelEndSlide | undefined,
) {
    if (nextMedia) return `Next ${mediaKindLabel(nextMedia)}`;
    return endSlide?.label ?? "Next photo";
}

function endSlideLayout(
    count: number,
    index: number,
    endSlide: MediaPanelEndSlide | undefined,
) {
    if (!endSlide) return { slideCount: count, onEndSlide: false };
    return { slideCount: count + 1, onEndSlide: index >= count };
}

function EndSlideFrame({
    endSlide,
    mounted,
}: {
    endSlide: MediaPanelEndSlide | undefined;
    mounted: boolean;
}) {
    if (!endSlide) return null;
    return (
        <div className="relative h-full w-full flex-none snap-center overflow-hidden">
            {mounted ? endSlide.content : null}
        </div>
    );
}

function EndSlideThumbnail({
    endSlide,
    onSelect,
}: {
    endSlide: MediaPanelEndSlide | undefined;
    onSelect: () => void;
}) {
    if (!endSlide) return null;
    return (
        <button
            type="button"
            aria-label={endSlide.label}
            onClick={onSelect}
            className="relative size-14 shrink-0 cursor-pointer overflow-hidden rounded-nested focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-true-white"
        >
            {endSlide.thumbnail}
        </button>
    );
}

export function DogFullBleedMediaPanel({
    scrollRestorationId,
    mediaItems,
    alt,
    onOpenFullscreen,
    index,
    onIndexChange,
    priority = false,
    active = true,
    mountGallery = true,
    endSlide,
    compact,
}: {
    scrollRestorationId: string;
    mediaItems: MediaItem[];
    alt: string;
    onOpenFullscreen: (index: number) => void;
    index: number;
    onIndexChange: (index: number) => void;

    priority?: boolean;
    active?: boolean;
    mountGallery?: boolean;
    endSlide?: MediaPanelEndSlide;
    compact?: boolean;
}) {
    const observed = useRef({ index: 0, width: 0, active });

    const [playingIndex, setPlayingIndex] = useState<number | null>(null);

    const [scrollTarget, setScrollTarget] = useState<number | null>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const scrollFrame = useRef<number | null>(null);
    const scrollSettle = useRef<ReturnType<typeof setTimeout> | null>(null);
    const count = mediaItems.length;
    const { slideCount, onEndSlide } = endSlideLayout(count, index, endSlide);

    const firstPhotoUrl = mediaItems.find((m) => !isVideoMedia(m))?.url ?? null;

    const width = useLayoutBox(trackRef)?.width ?? 0;

    useUnmountLayout(() => {
        if (scrollFrame.current !== null)
            cancelAnimationFrame(scrollFrame.current);
        if (scrollSettle.current !== null) clearTimeout(scrollSettle.current);
        scrollFrame.current = null;
        scrollSettle.current = null;
    });

    useUnsafeLayoutEffect(() => {
        const track = trackRef.current;
        const clientWidth = track?.clientWidth ?? 0;
        const resumed = active && !observed.current.active;
        observed.current.active = active;
        const resized = observed.current.width !== clientWidth;
        observed.current.width = clientWidth;
        if (
            !track ||
            clientWidth === 0 ||
            (!resized && !resumed && observed.current.index === index)
        )
            return;
        observed.current.index = index;
        track.scrollTo({
            left: index * track.clientWidth,
            behavior: "instant",
        });
    }, [index, width, active]);

    const onScroll = () => {
        if (scrollFrame.current !== null) return;
        scrollFrame.current = requestAnimationFrame(() => {
            scrollFrame.current = null;
            const el = trackRef.current;
            if (
                !el ||
                !observed.current.active ||
                el.clientWidth === 0 ||
                el.clientWidth !== observed.current.width
            )
                return;

            const next = Math.max(
                0,
                Math.min(
                    slideCount - 1,
                    Math.round(el.scrollLeft / el.clientWidth),
                ),
            );

            const changed = next !== observed.current.index;
            if (changed) {
                observed.current.index = next;
                onIndexChange(next);
                setPlayingIndex(null);
            }

            if (scrollTarget !== null && (changed || next === scrollTarget)) {
                setScrollTarget(null);
            }
        });

        if (scrollSettle.current !== null) clearTimeout(scrollSettle.current);
        scrollSettle.current = setTimeout(() => {
            scrollSettle.current = null;
            setScrollTarget((t) => (t !== null ? null : t));
        }, 150);
    };

    const scrollToIndex = (i: number) => {
        const el = trackRef.current;
        if (!el) return;

        if (i === index && el.scrollLeft === i * el.clientWidth) return;

        setScrollTarget(i);

        const far = Math.abs(i - index) > MOUNT_RADIUS;
        const smooth =
            !far &&
            !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        el.scrollTo({
            left: i * el.clientWidth,
            behavior: smooth ? "smooth" : "auto",
        });
    };

    const arrow =
        "absolute top-1/2 z-20 hidden size-8 after:absolute after:-inset-1.5 -translate-y-1/2 items-center justify-center rounded-full bg-true-black/15 text-true-white backdrop-blur-sm transition-colors md:flex [@media(hover:hover)]:hover:bg-true-black/25 [@media(hover:hover)]:hover:text-true-white";

    const activeIsVideo = mediaItems[index]
        ? isVideoMedia(mediaItems[index])
        : false;
    const isPlaying = activeIsVideo && playingIndex === index;
    const previousMedia = mediaItems[index - 1];
    const nextMedia = mediaItems[index + 1];

    return (
        <div className="relative h-[calc(100%+var(--listing-media-bottom-inset,0px))] w-full overflow-hidden bg-doberman-black">
            <div
                ref={trackRef}
                data-scroll-restoration-id={scrollRestorationId}
                onScroll={onScroll}
                className="flex h-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden scrollbar-none"
            >
                {mediaItems.map((m, i) => {
                    const isVid = isVideoMedia(m);
                    const playingHere = isVid && playingIndex === i;

                    const mountMedia = slideMediaMounted({
                        mountGallery,
                        index,
                        slideIndex: i,
                        scrollTarget,
                    });
                    const backdrop =
                        mountMedia && (isVid ? firstPhotoUrl : m.url);
                    return (
                        <div
                            key={`${i}-${m.url}`}
                            ref={markPhotoOrientations}
                            onLoadCapture={(e) =>
                                markPhotoOrientation(e.target)
                            }
                            className="relative h-full w-full flex-none snap-center overflow-hidden"
                        >
                            {backdrop && (
                                <div
                                    aria-hidden="true"
                                    className={cn(
                                        styles.backdrop,
                                        "pointer-events-none absolute inset-0 scale-110 bg-cover bg-center blur-xs brightness-60",
                                    )}
                                    style={
                                        {
                                            "--listing-backdrop":
                                                panelFillBackgroundImage(
                                                    backdrop,
                                                    true,
                                                ),
                                        } as CSSProperties
                                    }
                                />
                            )}
                            <div className="absolute inset-0 bottom-[var(--listing-media-bottom-inset,0px)]">
                                {!mountMedia ? null : playingHere ? (
                                    <ListingInlineVideo
                                        key={`video-${i}-${m.url}`}
                                        src={m.url}
                                        label={`${alt} video ${i + 1}`}
                                        onEnded={() => setPlayingIndex(null)}
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        aria-label={
                                            isVid
                                                ? `Play ${alt} video ${i + 1}`
                                                : `View ${alt} photo ${i + 1} full screen`
                                        }
                                        onClick={() => {
                                            if (isVid) setPlayingIndex(i);
                                            else onOpenFullscreen(i);
                                        }}
                                        className={cn(
                                            "absolute inset-0 group-data-[photo-viewer-active]:invisible after:pointer-events-none after:absolute after:inset-1 after:rounded-sm focus-visible:after:ring-2 focus-visible:after:ring-true-black focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-true-white",
                                            isVid
                                                ? "cursor-pointer"
                                                : "cursor-zoom-in",
                                        )}
                                    >
                                        <MediaThumbnail
                                            media={m}
                                            alt={`${alt} photo ${i + 1}`}
                                            listingMedia
                                            width={1200}
                                            height={1500}
                                            priority={priority && i === 0}
                                            loading={i === 0 ? "eager" : "lazy"}
                                            loadingPlaceholder={
                                                i === index ? (
                                                    <PhotoLoadingIndicator />
                                                ) : undefined
                                            }
                                            className="h-full w-full"
                                            mediaClassName="object-contain lg:landscape:data-[orientation=square]:object-cover"
                                        />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
                <EndSlideFrame
                    endSlide={endSlide}
                    mounted={slideMediaMounted({
                        mountGallery,
                        index,
                        slideIndex: count,
                        scrollTarget,
                    })}
                />
            </div>

            {slideCount > 1 && index > 0 && (
                <button
                    type="button"
                    aria-label={`Previous ${previousMedia ? mediaKindLabel(previousMedia) : "photo"}`}
                    onClick={() => scrollToIndex(index - 1)}
                    className={cn(arrow, "left-4")}
                >
                    <ChevronLeftIcon className="size-5" />
                </button>
            )}
            {slideCount > 1 && index < slideCount - 1 && (
                <button
                    type="button"
                    aria-label={nextArrowLabel(nextMedia, endSlide)}
                    onClick={() => scrollToIndex(index + 1)}
                    className={cn(arrow, "right-4")}
                >
                    <ChevronRightIcon className="size-5" />
                </button>
            )}

            {mountGallery && slideCount > 1 && !isPlaying && !onEndSlide && (
                <div className="absolute inset-x-0 bottom-[var(--listing-media-bottom-inset,0px)] z-10 transition-opacity duration-200 ease-out group-data-[photo-viewer-active]:opacity-0">
                    <div className="pointer-events-none absolute inset-0 bottom-[calc(-1*var(--listing-media-bottom-inset,0px))] bg-gradient-to-t from-true-black/60 via-true-black/25 to-transparent" />
                    <div
                        data-scroll-restoration-id={`${scrollRestorationId}-filmstrip`}
                        className="relative overflow-x-auto scrollbar-none"
                    >
                        <div
                            className={cn(
                                "mx-auto flex w-fit",
                                compact
                                    ? "gap-1.5 px-4 pb-3 pt-6 md:gap-2 md:pb-4"
                                    : "gap-2 px-4 pb-4 pt-12 max-md:pb-7",
                            )}
                        >
                            {mediaItems.map((media, i) => (
                                <MediaThumbnailButton
                                    key={`thumb-${media.url}-${i}`}
                                    ref={
                                        i === index
                                            ? revealThumbnail
                                            : undefined
                                    }
                                    media={media}
                                    listingMedia
                                    label={`Show ${mediaKindLabel(media)} ${i + 1}`}
                                    selected={i === index}
                                    onClick={() => scrollToIndex(i)}
                                    size={compact ? "xs" : "sm"}
                                />
                            ))}
                            <EndSlideThumbnail
                                endSlide={endSlide}
                                onSelect={() => scrollToIndex(count)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function markPhotoOrientation(target: EventTarget | null) {
    if (!(target instanceof HTMLImageElement) || target.naturalWidth === 0)
        return;
    target.dataset.orientation = photoOrientation(
        target.naturalWidth / target.naturalHeight,
    );
}

function markPhotoOrientations(slide: HTMLDivElement | null) {
    slide?.querySelectorAll("img").forEach(markPhotoOrientation);
}

function photoOrientation(ratio: number) {
    if (Math.abs(ratio - 1) <= 0.05) return "square";
    return ratio > 1 ? "landscape" : "portrait";
}
